import { X, MapPin, Building2, Globe, Clock, Euro, Calendar } from 'lucide-react';
import type { Training } from '../../services/trainingLogApi';

interface Props {
    training: Training;
    onClose: () => void;
}

function formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function TrainingDetailModal({ training, onClose }: Props) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Training Details</h2>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Employee */}
                    {(training.EmployeeName || training.UserEmail) && (
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Employee</div>
                            <div className="text-sm text-gray-900 font-medium">
                                {training.EmployeeName || training.UserEmail}
                            </div>
                        </div>
                    )}

                    {/* Course Topic */}
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Course Topic</div>
                        <div className="text-sm text-gray-900">{training.CourseTopic}</div>
                    </div>

                    {/* Date & Duration */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Date</div>
                                <div className="text-sm text-gray-900">
                                    {formatDate(training.StartDate)}
                                    {training.StartDate !== training.EndDate && ` - ${formatDate(training.EndDate)}`}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Duration</div>
                                <div className="text-sm text-gray-900">{training.DurationHours}h</div>
                            </div>
                        </div>
                    </div>

                    {/* Category & Type */}
                    <div className="flex gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700">
                            {training.Category}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                            training.InternalExternal === 'Internal'
                                ? 'bg-green-50 text-green-700'
                                : 'bg-orange-50 text-orange-700'
                        }`}>
                            {training.InternalExternal}
                        </span>
                        {training.IsSupplierTraining && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700">
                                Supplier
                            </span>
                        )}
                        {training.IsWebTraining && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-cyan-50 text-cyan-700">
                                Web
                            </span>
                        )}
                    </div>

                    {/* Cost */}
                    {training.CostsEur != null && training.CostsEur > 0 && (
                        <div className="flex items-start gap-2">
                            <Euro className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Cost</div>
                                <div className="text-sm text-gray-900">
                                    {training.CostsCurrency && training.CostsCurrency !== 'EUR' && training.CostsOriginal != null
                                        ? `${training.CostsOriginal.toLocaleString('de-CH')} ${training.CostsCurrency} (€ ${training.CostsEur.toLocaleString('de-CH')})`
                                        : `€ ${training.CostsEur.toLocaleString('de-CH')}`
                                    }
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Organizer */}
                    {training.TrainingOrganizer && (
                        <div className="flex items-start gap-2">
                            <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Organizer</div>
                                <div className="text-sm text-gray-900">{training.TrainingOrganizer}</div>
                            </div>
                        </div>
                    )}

                    {/* Place */}
                    {training.PlaceOfTraining && (
                        <div className="flex items-start gap-2">
                            {training.IsWebTraining ? <Globe className="w-4 h-4 text-gray-400 mt-0.5" /> : <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />}
                            <div>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Place</div>
                                <div className="text-sm text-gray-900">{training.PlaceOfTraining}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end p-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
